'use client';

import { MessagePrimitive, useAssistantState, useAuiState } from '@assistant-ui/react';
import { type JSX } from 'react';

import { isBrowseLimitError, isContextLengthError, isRateLimitError } from '../../_utils/constants';
import { ChatErrorCard } from './ChatErrorCard';
import { ConversationTooLongNotice } from './ConversationTooLongNotice';
import { MarkdownText } from './MarkdownText';
import { ReasoningPart, ReasoningGroup } from './ReasoningPart';
import { ToolFallback } from './ToolFallback';

/**
 * Text bubble wrapper for individual text parts.
 * Each text part gets its own bubble with consistent styling.
 * Returns null when the text part is empty (e.g. on error responses).
 */
function TextBubble(): JSX.Element | null {
    const text = useAuiState(({ part }) => (part.type === 'text' ? part.text : ''));

    if (!text) {
        return null;
    }

    return (
        <div className="max-w-[90%] bg-neutral-50 border border-gray-200 rounded-lg px-4 py-3">
            <div className="text-sm text-gray-900 leading-relaxed">
                <MarkdownText />
            </div>
        </div>
    );
}

/**
 * Returns true when this assistant message ended in an error. Browse limit errors are
 * handled separately by UsageLimitMessage, so we exclude them here.
 */
function useIsErrored(): boolean {
    return useAssistantState(({ message }) => {
        const status = message.status;
        if (status?.type !== 'incomplete' || status.reason !== 'error') return false;
        if (isBrowseLimitError(status.error)) return false;
        if (isRateLimitError(status.error)) return false;
        return true;
    });
}

/**
 * Returns true when this assistant message errored because the conversation exceeded the
 * model's context window. The stream surfaces this as the CONTEXT_LENGTH_EXCEEDED sentinel.
 */
function useIsContextLengthError(): boolean {
    return useAssistantState(({ message }) => {
        const status = message.status;
        if (status?.type !== 'incomplete' || status.reason !== 'error') return false;
        return isContextLengthError(status.error);
    });
}

/**
 * Assistant message component.
 * Renders each part with appropriate styling - text parts get individual bubbles.
 * Context-window errors render a "conversation too long" notice; other (non-browse-limit)
 * errors render the generic ChatErrorCard.
 */
export function AssistantMessage(): JSX.Element {
    const isContextLengthError = useIsContextLengthError();
    const isErrored = useIsErrored();

    if (isContextLengthError) {
        return <ConversationTooLongNotice />;
    }

    if (isErrored) {
        return <ChatErrorCard />;
    }

    return (
        <MessagePrimitive.Root className="py-3" data-role="assistant">
            <div className="space-y-2">
                <MessagePrimitive.Parts
                    components={{
                        Text: TextBubble,
                        Reasoning: ReasoningPart,
                        ReasoningGroup: ReasoningGroup,
                        tools: { Fallback: ToolFallback },
                    }}
                />
            </div>
        </MessagePrimitive.Root>
    );
}
